import { appEvents, type BudgetExceededPayload } from '@/lib/app-events';

/**
 * A `fetch` for requests that do not go through `apiClient` — today the chat
 * stream, which the AI SDK's `DefaultChatTransport` issues itself.
 *
 * `apiClient` turns a 402 `budget_exceeded` into the app-wide
 * `budget-exceeded` event that opens `BudgetExceededDialog`. The chat
 * transport bypassed it, so a chat 402 opened nothing and the SDK printed the
 * raw JSON body as the chat's error. This emits the same event and throws a
 * typed `BudgetExceededError`, which the SDK surfaces as the chat's `error`
 * and `onError` argument, so the caller can tell a limit from a failure.
 *
 * Any other response is returned untouched.
 */
export class BudgetExceededError extends Error {
  readonly payload: BudgetExceededPayload;

  constructor(payload: BudgetExceededPayload) {
    super(payload.message);
    this.name = 'BudgetExceededError';
    this.payload = payload;
  }
}

export function isBudgetExceededError(
  err: unknown
): err is BudgetExceededError {
  return err instanceof BudgetExceededError;
}

async function budgetPayload(
  response: Response
): Promise<BudgetExceededPayload | null> {
  if (response.status !== 402) return null;
  try {
    const body = (await response.clone().json()) as { type?: string };
    return body?.type === 'budget_exceeded'
      ? (body as BudgetExceededPayload)
      : null;
  } catch {
    return null;
  }
}

export async function budgetAwareFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const response = await fetch(input, init);
  const payload = await budgetPayload(response);
  if (!payload) return response;
  const url =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.href
        : input.url;
  appEvents.emit({
    type: 'budget-exceeded',
    payload,
    endpoint: new URL(url, window.location.origin).pathname,
  });
  throw new BudgetExceededError(payload);
}
